/**
 *
 * @module dustLogUtility
 *
 */

import Check from 'check-types';

import Logging from './../../../logging/entry';

import DustUrnReference from './dustUrnReference';
import DustCategory from './dustCategory';

import InvalidArgumentException from './../../exception/invalidArgumentException';

const {
    instanceStrict,
    nonEmptyString,
    object,
    nonEmptyObject
} = Check.assert;

/**
 *
 * @class DustLogUtility
 * @since 3.4.0
 * @desc helper code that collects server and client data until it is assembled in DustSink#createDustEvent
 *
 */
export default class DustLogUtility {

    /**
     *
     * @param {Object} options
     * @param {SDK.Logger} options.logger
     * @param {String} options.source
     * @param {String} options.urn
     * @param {Object} [options.data={}]
     * @param {String} [options.method]
     * @param {Object} [options.payload={}]
     * @param {String} [options.category]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(options) {

        const errorMessage = {
            ctor: 'DustLogUtility(options)',
            get options() {
                return `${this.ctor} options should be a non-empty Object`;
            },
            get logger() {
                return `${this.ctor} needs SDK.Logger instance.`;
            },
            get source() {
                return `${this.ctor} options.source should be a String with length.`;
            },
            get urn() {
                return `${this.ctor} options.urn should be a String with length`;
            },
            get data() {
                return `${this.ctor} options.data should be an Object`;
            },
            get method() {
                return `${this.ctor} options.method should be a String with length`;
            },
            get payload() {
                return `${this.ctor} options.payload should be an Object`;
            },
            get category() {
                return `${this.ctor} options.category should be a String with length`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { logger, source, urn, data = {}, method, payload = {}, category } = options;

        instanceStrict(logger, Logging.Logger, errorMessage.logger, InvalidArgumentException);
        nonEmptyString(source, errorMessage.source, InvalidArgumentException);
        nonEmptyString(urn, errorMessage.urn, InvalidArgumentException);
        object(data, errorMessage.data, InvalidArgumentException);

        if (Check.assigned(method)) {
            nonEmptyString(method, errorMessage.method, InvalidArgumentException);
        }

        object(payload, errorMessage.payload, InvalidArgumentException);

        if (Check.assigned(category)) {
            nonEmptyString(category, errorMessage.category, InvalidArgumentException);
        }

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {String}
         * @desc the constructor name of the file where this event took place
         *
         */
        this.source = source;

        /**
         *
         * @access private
         * @type {String}
         * @desc the urn corresponding to this event
         *
         */
        this.urn = urn;

        /**
         *
         * @access private
         * @type {Object}
         * @desc stores client data like errors, provider info, query info etc
         *
         */
        this.data = data;

        /**
         *
         * @access private
         * @type {Object}
         * @desc the payload generated in the client
         *
         */
        this.payload = payload;

        /**
         *
         * @access private
         * @type {String}
         * @desc stores the category of this event
         * @note for `Glimpse` and `Personalization` events this should be supplied when creating `DustLogUtility` for
         * all other events it is constructed in `DustLogUtility.generateCategory()`
         *
         */
        this.category = category;

        /**
         *
         * @access private
         * @type {Object}
         * @desc stores server data - only the method string gets passed in via the constructor
         *
         */
        this.server = method ? Object.assign({}, { method }) : {};

        /**
         *
         * @access private
         * @type {String}
         * @desc The timestamp of when the event was logged, in ISO 8601/RFC-3339 format
         *
         */
        this.timestamp = new Date().toISOString();

        /**
         *
         * @access private
         * @type {String}
         * @desc needs to be {name} for the logger but eventually turns into {event} - the urn for this dustEvent
         * @note the user should be passing in a reference to the full urn or reference to the urn in
         * `SDK.Services.Internal.Dust.DustUrnReference`
         *
         */
        this.name = this.urn;

        /**
         *
         * @access private
         * @type {Number}
         *
         */
        this.level = 3;

        /**
         *
         * @access private
         * @type {Boolean}
         * @desc flag that indicates this is a `SDK.Internal.Dust.DustEvent` event - parsed in `SDK.Logging.Logger`
         *
         */
        this.isPublic = true;

        /**
         *
         * @access private
         * @type {Array}
         * @desc list of errors including service and/or invalid arguments
         *
         */
        this.errors = [];

    }

    /**
     *
     * @access public
     * @desc entry point for creating a DustEvent. This will piece together the first parts of the DustPayload and
     * send them through the logger flow until DustSink can assemble the final DustEvent.
     * @note (try/catch and finally) ensure this will always resolve, it is a safety mechanism to prevent any methods
     * in the app from failing while chaining this method
     * @returns {Promise<Void>}
     *
     */
    log() {

        try {

            const { generateLogEvent, isPublic, logger, source } = this;

            const logEvent = generateLogEvent.call(this);

            return logger.log(source, logEvent, isPublic).catch((ex) => {

                return logger.warn(ex, logEvent);

            }).finally(() => {

                return Promise.resolve();

            });

        } catch (ex) {

            return Promise.resolve();

        }

    }

    /**
     *
     * @access public
     * @param {Array|Object} error
     * @desc pushes error(s) into the errors array
     * @note returns an unmodified version of the error
     * @returns {Array|Object}
     *
     */
    captureErrors(error) {

        const { errors } = this;

        if (Check.nonEmptyArray(error)) {
            errors.push(...error);
        } else if (Check.object(error) && Check.nonEmptyObject(error.data)) {
            errors.push(error.data);
        }

        return error;

    }

    /**
     *
     * @param {Object} data
     * @desc client generated data about the event like a query, the providerType (bamIdentity), etc and errors
     *
     */
    logData(data = {}) {

        if (Check.nonEmptyObject(data)) {
            this.data = Object.assign(this.data, data);
        }

    }

    /**
     *
     * @param {Object} options
     * @param {Object} options.response
     * @param {String} options.transactionId
     * @param {Array} options.errors
     * @desc parses a response from the client
     *
     */
    parseResponse(options) {

        if (Check.object(options)) {

            const { server, timestamp } = this;
            const { status } = options.response;
            const { transactionId, errors } = options;

            this.parsePayload();

            server.requestId = Check.nonEmptyString(transactionId) ? transactionId : undefined;
            server.statusCode = status;
            server.roundTripTime = Date.now() - Date.parse(timestamp);

            if (Check.assigned(errors)) {
                this.captureErrors(errors);
            }

        }

    }

    /** region private */

    /**
     *
     * @access private
     * @desc parses payload to get host and path
     * @note urlRegex parses the payload Url to be able to extract the host and path reliably
     *
     */
    parsePayload() {

        const { payload, server } = this;

        if (Check.nonEmptyString(payload.url)) {

            const urlRegex = '^(.*:)//?([www.]+)*([A-Za-z0-9-.]+)(:[0-9]+)?(.*)$';
            const regex = new RegExp(urlRegex, 'g');
            const parsedUrl = regex.exec(payload.url);

            if (Check.nonEmptyArray(parsedUrl)) {
                server.host = parsedUrl[3];
                server.path = parsedUrl[5].split('?')[0];
            }

        }

    }

    /**
     *
     * @access private
     * @desc assembles an object used in DustSink where it gets transformed into a DustEvent
     * @returns {Object}
     *
     */
    generateLogEvent() {

        const { data, errors, isPublic, level, name, payload, server, timestamp } = this;

        const isServiceEvent = Check.nonEmptyObject(payload);
        const hasErrors = Check.nonEmptyArray(errors);

        const logEvent = {
            extraData: {
                category: this.generateCategory({ isServiceEvent, hasErrors }),
                timestamp
            },
            isPublic,
            name,
            level
        };

        const { extraData } = logEvent;

        if (isServiceEvent) {
            extraData.server = server;
        }

        if (Check.nonEmptyObject(data)) {
            extraData.data = data;
        }

        if (hasErrors) {
            extraData.errors = errors;
        }

        return logEvent;

    }

    /**
     *
     * @access private
     * @param {Object} options
     * @param {Boolean} options.isServiceEvent
     * @param {Boolean} options.hasErrors
     * @desc sets the category of the event
     * @note this should only create a new category for general dust events -
     * `SDK.UserActivity.Personalization.PersonalizationEvent`'s and `SDK.UserActivity.Glimpse.GlimpseEvent`'s
     * should have their category created when the `DustLogUtility` instance is created
     * this is dynamic due to the fact that general Dust events can originate as a result of an API call or a Service
     * interaction and we don't know if it's successful until after the operation has completed
     * @example 'urn:bamtech:dust:bamsdk:event:service'
     *
     */
    generateCategory(options = {}) {

        const { category } = this;

        if (Check.not.assigned(category)) {

            const { isServiceEvent, hasErrors } = options;

            const eventName = isServiceEvent ? 'service' : 'api';
            const eventType = hasErrors ? 'error' : 'event';

            return `${DustUrnReference.bamSdk}${DustCategory[eventName][eventType]}`;

        }

        return category;

    }

}



// WEBPACK FOOTER //
// ./src/services/internal/dust/dustLogUtility.js